import { Box, IconButton, Tooltip, Typography } from "@mui/material"
import { DraftOutlined, KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { useState } from "react"
import { FormProvider, useWatch } from "react-hook-form"

import { SalesOrderFormLines } from "./SalesOrderFormLines"
import { SalesOrderLogisticsForm } from "./SalesOrderLogisticsForm"

import { Button } from "@/components/common/Button"
import CancelDialog from "@/components/common/Modals/CancelDialog"
import { FormSelect } from "@/components/UiKit/FormControls/FormSelect"
import type { SalesOrder } from "@/graphql/codegen/graphql"
import { useGetWarehouseOptions } from "@/hooks/useGetWarehouseOptions"
import { SalesOrderStatusChip } from "@/screens/CustomerOrders/components/CustomerOrderListPage/SalesOrderStatusChip"
import { SalesOrderRowActions } from "@/screens/CustomerOrders/components/OpenSalesOrdersTabPanel/SalesOrderRowActions"
import { SalesOrderCancelConfirmationDialog } from "@/screens/CustomerOrders/components/SalesOrderCancelDialog/SalesOrderCancelConfirmationDialog"
import { SalesOrderDocumentsDialog } from "@/screens/CustomerOrders/components/SalesOrderDocuments/SalesOrderDocumentsDialog"
import { UploadSalesOrderDocumentDialog } from "@/screens/CustomerOrders/components/SalesOrderDocuments/UploadSalesOrderDocumentDialog"
import { SalesOrderStatusFormDialog } from "@/screens/CustomerOrders/components/SalesOrderStatusDialog/SalesOrderStatusFormDialog"
import { useSalesOrderLogisticsForm } from "@/screens/CustomerOrders/hooks/useSalesOrderLogisticsForm"
import { useSalesOrderPermissions } from "@/screens/CustomerOrders/hooks/useSalesOrderPermissions"
import { SalesOrderDialogType } from "@/screens/CustomerOrders/types"
import { isSalesOrderConfirmable, isSalesOrderEditable } from "@/screens/CustomerOrders/utils"

const SEND_CONFIRMATION_REQUIRED_FIELDS_MESSAGE = "Incoterms, Incoterms Location, Ship Via and Freight required"

interface SalesOrderRowProps {
  salesOrder: SalesOrder
  shipToLocationId: string
  expanded: boolean
  onExpandToggle: () => void
  onLinesSaved?: () => void
}

export const SalesOrderRow: FC<SalesOrderRowProps> = ({
  salesOrder,
  shipToLocationId,
  expanded,
  onExpandToggle,
  onLinesSaved,
}) => {
  const [showDialog, setShowDialog] = useState<SalesOrderDialogType | null>(null)

  const { warehouseOptions } = useGetWarehouseOptions({ isActive: true })
  const { isReadonly: isPermissionReadonly } = useSalesOrderPermissions()
  const isEditable = isSalesOrderEditable(salesOrder.status)
  const isReadonly = isPermissionReadonly || !isEditable

  const {
    methods,
    handleSave,
    handleSendConfirmation,
    isSaving,
    handleDiscard,
    isDiscardConfirmationOpen,
    setIsDiscardConfirmationOpen,
    handleRequestedShipOnChange,
    handleRequestedDeliveryDateChange,
    handleAsapSelected,
  } = useSalesOrderLogisticsForm({ salesOrder, shipToLocationId, onLinesSaved })

  const {
    control,
    formState: { isDirty, isValid, isSubmitting },
  } = methods

  const [incoterms, incotermsLocationId, shipViaCompanyId, freightTerms] = useWatch({
    control,
    name: ["incoterms", "incotermsLocationId", "shipViaCompanyId", "freightTerms"],
  })
  const hasRequiredConfirmationFields = Boolean(incoterms && incotermsLocationId && shipViaCompanyId && freightTerms)
  const canSendConfirmation = isSalesOrderConfirmable(salesOrder.status) && hasRequiredConfirmationFields && isValid

  const handleStatusUpdated = () => {
    setShowDialog(null)
    onLinesSaved?.()
  }

  const handleDiscardConfirm = () => {
    methods.reset()
    setIsDiscardConfirmationOpen(false)
  }

  return (
    <FormProvider {...methods}>
      <Box className='rounded-md border border-solid border-gray-400'>
        <Box className='flex flex-nowrap items-center justify-between gap-4 rounded-md bg-white p-6'>
          <Box className='flex min-w-0 flex-1 items-center gap-4'>
            <Box className='flex shrink-0 items-center gap-2'>
              <Typography variant='body1' className='min-w-0 truncate text-lg text-gray-800'>
                SO#: {salesOrder.publicId}
              </Typography>
              <SalesOrderStatusChip status={salesOrder.status} />
            </Box>
            <Box className='flex shrink-0 items-center gap-2'>
              <Typography variant='body2' className='shrink-0 whitespace-nowrap text-gray-700'>
                Ship From:
              </Typography>
              <Box className='w-72'>
                <FormSelect
                  control={control}
                  name='shipFromWarehouseAreaId'
                  options={warehouseOptions}
                  placeholder='Select...'
                  disabled={isReadonly}
                  className='w-full'
                  data-testid={`customer-orders.so-row.ship-from-select-${salesOrder.salesOrderId}`}
                />
              </Box>
            </Box>
          </Box>

          <Box className='flex shrink-0 flex-nowrap items-center justify-end gap-4'>
            {isEditable && (
              <>
                <Button
                  disabled={!isDirty || isReadonly || isSubmitting}
                  loading={isSaving}
                  onClick={handleSave}
                  data-testid={`customer-orders.so-row.save-changes-button-${salesOrder.salesOrderId}`}
                >
                  Save Changes
                </Button>
                <Button
                  appearance='outlined'
                  disabled={!isDirty || isReadonly}
                  onClick={handleDiscard}
                  data-testid={`customer-orders.so-row.discard-changes-button-${salesOrder.salesOrderId}`}
                >
                  Discard Changes
                </Button>
                <Tooltip title={hasRequiredConfirmationFields ? "" : SEND_CONFIRMATION_REQUIRED_FIELDS_MESSAGE}>
                  <span>
                    <Button
                      disabled={!canSendConfirmation || isReadonly || isSubmitting || isSaving}
                      onClick={handleSendConfirmation}
                      data-testid={`customer-orders.so-row.send-confirmation-button-${salesOrder.salesOrderId}`}
                    >
                      Send SO Confirmation
                    </Button>
                  </span>
                </Tooltip>
              </>
            )}
            <IconButton
              size='small'
              onClick={() => setShowDialog(SalesOrderDialogType.ViewDocuments)}
              aria-label='SO document'
              data-testid={`customer-orders.so-row.document-button-${salesOrder.salesOrderId}`}
            >
              <DraftOutlined />
            </IconButton>
            <SalesOrderRowActions
              salesOrder={salesOrder}
              onUpload={() => setShowDialog(SalesOrderDialogType.UploadDocuments)}
              onUpdateStatus={() => setShowDialog(SalesOrderDialogType.UpdateStatus)}
              onCancel={() => setShowDialog(SalesOrderDialogType.CancelSalesOrder)}
            />
            <IconButton
              size='small'
              aria-label={expanded ? "collapse SO" : "expand SO"}
              onClick={onExpandToggle}
              data-testid={`customer-orders.so-row.expand-button-${salesOrder.salesOrderId}`}
            >
              {expanded ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
            </IconButton>
          </Box>
        </Box>

        {expanded && (
          <Box className='flex flex-col rounded-b-md'>
            <Box className='bg-primary-100 p-6'>
              <SalesOrderLogisticsForm
                salesOrder={salesOrder}
                onRequestedShipOnChange={handleRequestedShipOnChange}
                onRequestedDeliveryDateChange={handleRequestedDeliveryDateChange}
                onAsapSelected={handleAsapSelected}
              />
            </Box>
            <Box className='rounded-b-md bg-white p-6'>
              <SalesOrderFormLines salesOrder={salesOrder} />
            </Box>
          </Box>
        )}

        {isDiscardConfirmationOpen && (
          <CancelDialog
            title='Discard changes?'
            message='Discarding now will remove all unsaved changes to this sales order.'
            closeLabel='Go Back'
            confirmLabel='Discard Changes'
            onClose={() => setIsDiscardConfirmationOpen(false)}
            onConfirm={handleDiscardConfirm}
          />
        )}

        {showDialog === SalesOrderDialogType.ViewDocuments && (
          <SalesOrderDocumentsDialog salesOrder={salesOrder} onClose={() => setShowDialog(null)} />
        )}
        {showDialog === SalesOrderDialogType.UpdateStatus && (
          <SalesOrderStatusFormDialog
            salesOrder={salesOrder}
            onClose={() => setShowDialog(null)}
            onSubmit={handleStatusUpdated}
          />
        )}
        {showDialog === SalesOrderDialogType.CancelSalesOrder && (
          <SalesOrderCancelConfirmationDialog
            salesOrder={salesOrder}
            onClose={() => setShowDialog(null)}
            onSubmit={handleStatusUpdated}
          />
        )}
        {showDialog === SalesOrderDialogType.UploadDocuments && (
          <UploadSalesOrderDocumentDialog
            salesOrderId={salesOrder.salesOrderId}
            onClose={() => setShowDialog(null)}
            onSuccess={() => setShowDialog(null)}
          />
        )}
      </Box>
    </FormProvider>
  )
}
